// Script Adherence Zod Schemas
// Validation schemas for script adherence feature
// Date: 2025-01-05

import { z } from 'zod/v4';

// ============================================================================
// Base Enums
// ============================================================================

export const ScriptCategorySchema = z.enum([
  'sales_call',
  'support_call',
  'sales_chat',
  'complaint',
  'custom',
]);

export const PhraseTypeSchema = z.enum(['required', 'optional', 'forbidden']);

export const MatchTypeSchema = z.enum(['exact', 'semantic', 'regex']);

/**
 * Тип и способ сверки критерия, когда вызывающий их не назвал: критерий
 * обязателен и оценивается моделью по смыслу. Единственное место этих умолчаний
 * — API, `ScriptRepository` (запись в D1) и перевод заготовок (#852) берут их
 * отсюда, иначе посев и сравнение с шаблоном разошлись бы молча.
 */
export const DEFAULT_PHRASE_TYPE = 'required' as const;
export const DEFAULT_MATCH_TYPE = 'semantic' as const;

export const SpeakerSchema = z.enum(['agent', 'customer']);

// ============================================================================
// Weight scale — SSOT
// ============================================================================

/**
 * Шкала веса шага и критерия: целые, 100 = обычная важность.
 *
 * Это шкала, на которой уже лежат данные (`script_steps.weight`,
 * `script_step_phrases.weight`), сиды и промпт оценки соответствия — тот
 * печатает вес модели только когда он ОТЛИЧАЕТСЯ от 100
 * (`packages/prompt-builder/src/script-adherence.ts:240,247`). Пока API
 * принимал множитель 0.1–2.0, шаг из интерфейса ложился в базу как `1` и
 * подавался модели как «в сто раз менее важный», а шаг из AI-разбора со
 * штатным весом 100 не проходил валидацию вовсе.
 *
 * Любой слой, которому нужен диапазон, берёт его отсюда, а не пишет свои
 * числа: промпт разбора строит из них описание для модели, интерфейс —
 * границы слайдера.
 */
export const SCRIPT_WEIGHT_MIN = 10;
export const SCRIPT_WEIGHT_MAX = 200;
export const SCRIPT_WEIGHT_NORMAL = 100;

// ============================================================================
// Script Schemas (D1 - Configuration)
// ============================================================================

export const ScriptSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  name: z.string().min(1).max(255),
  description: z.string().max(1000).optional(),
  category: ScriptCategorySchema,
  isActive: z.boolean(),
  version: z.number().int().positive(),
  createdByUserId: z.string().uuid(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime().optional(),
});

export const ScriptStepPhraseSchema = z.object({
  id: z.string().uuid(),
  stepId: z.string().uuid(),
  phraseText: z.string().min(1).max(500),
  phraseType: PhraseTypeSchema,
  matchType: MatchTypeSchema,
  semanticThreshold: z.number().min(0).max(1),
  weight: z.number().int().min(SCRIPT_WEIGHT_MIN).max(SCRIPT_WEIGHT_MAX), // 100 = обычная важность
  createdAt: z.string().datetime(),
});

export const ScriptStepSchema = z.object({
  id: z.string().uuid(),
  scriptId: z.string().uuid(),
  stepNumber: z.number().int().positive(),
  name: z.string().min(1).max(255),
  description: z.string().max(1000).optional(),
  isRequired: z.boolean(),
  orderEnforced: z.boolean(),
  weight: z.number().int().min(SCRIPT_WEIGHT_MIN).max(SCRIPT_WEIGHT_MAX), // 100 = обычная важность
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime().optional(),
});

export const ScriptStepWithPhrasesSchema = ScriptStepSchema.extend({
  phrases: z.array(ScriptStepPhraseSchema),
});

export const ScriptWithStepsSchema = ScriptSchema.extend({
  steps: z.array(ScriptStepWithPhrasesSchema),
});

export const ProjectScriptSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  scriptId: z.string().uuid(),
  isDefault: z.boolean(),
  assignedByUserId: z.string().uuid(),
  assignedAt: z.string().datetime(),
});

// ============================================================================
// Script Adherence Result Schemas (Postgres - Analysis Results)
// ============================================================================

/**
 * Ceiling for a step's evidence quote — the bound this schema enforces and the
 * one the analyzer must trim to.
 *
 * Exported because the trim lives in another package
 * (`workers/analyze/script-adherence.ts`) and used to restate `2000` as its own
 * literal. Widen the schema and a hand-copied trim keeps clipping at the old
 * value; narrow it and every over-long quote fails validation on READ,
 * discarding an already-billed evaluation — the exact failure the trim exists
 * to prevent.
 */
export const STEP_EVIDENCE_MAX_LENGTH = 2000;

/**
 * A criterion is «almost never matched» when it was missed on at least this
 * share of the evaluations that owed its step, and on at least
 * `SCRIPT_CRITERION_ALMOST_NEVER_MIN_EVALUATIONS` of them. One criterion that
 * nobody can satisfy (a compound «…and quantity», #1735) makes its step fail
 * in nearly every call; the script-steps report (#879) marks it so the reader
 * blames the wording, not the managers. Shared with #1735, so the two screens
 * cannot disagree on what «almost never» means.
 */
export const SCRIPT_CRITERION_ALMOST_NEVER_RATE = 0.9;
export const SCRIPT_CRITERION_ALMOST_NEVER_MIN_EVALUATIONS = 20;

export const StepEvaluationResultSchema = z.object({
  stepId: z.string(),
  stepNumber: z.number().int().positive(),
  stepName: z.string(),
  isCompleted: z.boolean(),
  /**
   * Whether this step was OWED on this conversation — i.e. whether it counts
   * toward the adherence score and the step tally.
   *
   * A step marked `isRequired: false` in the script that never happened is not
   * a failure, it is a step nobody asked for: the editor promises it "costs
   * nothing when it does not happen". Such a step is excluded from the score,
   * so it must be excluded from everything that reads a failure into
   * `isCompleted: false` — the step tally, the missed-criteria list, the
   * supervisor's most-skipped-steps ranking, and its own colour on screen.
   *
   * Optional (absent) on rows stored before the field existed; treat a missing
   * value as `true`, which is how every step behaved then.
   */
  countsTowardScore: z.boolean().optional(),
  score: z.number().int().min(0).max(100),
  matchedRequired: z.number().int().min(0),
  totalRequired: z.number().int().min(0),
  matchedOptional: z.number().int().min(0),
  totalOptional: z.number().int().min(0),
  forbiddenDetected: z.boolean(),
  firstOccurrenceTimestamp: z.string().nullable().optional(),
  evidence: z.string().max(STEP_EVIDENCE_MAX_LENGTH).nullable().optional(),
});

export const MatchedPhraseSchema = z.object({
  phraseId: z.string(),
  phraseText: z.string(),
  /**
   * Step the matched criterion belongs to — the counterpart of
   * `MissedPhraseSchema.stepId`, so both halves of a step's breakdown can be
   * filtered the same way.
   *
   * The evaluating model neither returns it nor should: a criterion's step is
   * a fact of the script, resolved from `phraseId` while the evaluation is
   * enriched (`workers/analyze/script-adherence.ts`). Optional only because
   * rows stored before this field exists are read back through this schema;
   * a fresh evaluation always carries it.
   */
  stepId: z.string().optional(),
  /**
   * Criterion kind, copied from the script during enrichment. A FORBIDDEN
   * criterion is recorded here — a match is how a violation is registered —
   * so a renderer that shows this list as "what the agent achieved" needs to
   * tell the two apart. Optional for rows stored before the field existed.
   */
  phraseType: PhraseTypeSchema.optional(),
  matchedText: z.string(),
  similarityScore: z.number().min(0).max(1),
  timestamp: z.string().nullable().optional(),
  speaker: SpeakerSchema,
});

export const MissedPhraseSchema = z.object({
  phraseId: z.string(),
  phraseText: z.string(),
  stepId: z.string(),
  stepName: z.string(),
  isRequired: z.boolean(),
});

export const OutOfOrderStepSchema = z.object({
  stepId: z.string(),
  stepName: z.string(),
  expectedPosition: z.number().int().positive(),
  actualPosition: z.number().int().positive(),
  timestamp: z.string().nullable().optional(),
});

export const ScriptAdherenceResultSchema = z.object({
  id: z.string().uuid(),
  analysisId: z.string().uuid(),
  recordId: z.string().uuid(),
  scriptId: z.string(),
  scriptVersion: z.number().int().positive(),

  adherenceScore: z.number().int().min(0).max(100),
  stepsCompleted: z.number().int().min(0),
  stepsTotal: z.number().int().min(0),
  stepsInOrder: z.number().int().min(0),
  orderScore: z.number().int().min(0).max(100).optional(),

  stepResults: z.array(StepEvaluationResultSchema),
  matchedPhrases: z.array(MatchedPhraseSchema),
  missedPhrases: z.array(MissedPhraseSchema),
  outOfOrderSteps: z.array(OutOfOrderStepSchema),

  evaluationModel: z.string().optional(),
  evaluationTokensIn: z.number().int().optional(),
  evaluationTokensOut: z.number().int().optional(),
  evaluationCostCents: z.number().int().optional(),

  createdAt: z.string().datetime(),
});

// ============================================================================
// LLM Evaluation Schema (for structured output)
// ============================================================================

/**
 * Schema for LLM structured output response.
 * Used with OpenAI's JSON mode for script adherence evaluation.
 */
export const ScriptAdherenceEvaluationSchema = z.object({
  adherenceScore: z.number().int().min(0).max(100),
  orderScore: z.number().int().min(0).max(100).optional(),
  stepsCompleted: z.number().int().min(0),
  stepsTotal: z.number().int().min(0),
  stepsInOrder: z.number().int().min(0),
  stepResults: z.array(StepEvaluationResultSchema),
  matchedPhrases: z.array(MatchedPhraseSchema),
  missedPhrases: z.array(MissedPhraseSchema),
  outOfOrderSteps: z.array(OutOfOrderStepSchema),
});

// ============================================================================
// API Request Schemas
// ============================================================================

export const CreatePhraseRequestSchema = z.object({
  phraseText: z.string().min(1).max(500),
  phraseType: PhraseTypeSchema.default(DEFAULT_PHRASE_TYPE),
  matchType: MatchTypeSchema.default(DEFAULT_MATCH_TYPE),
  semanticThreshold: z.number().min(0).max(1).default(0.75),
  weight: z
    .number()
    .int()
    .min(SCRIPT_WEIGHT_MIN)
    .max(SCRIPT_WEIGHT_MAX)
    .default(SCRIPT_WEIGHT_NORMAL),
});

export const UpdatePhraseRequestSchema = z.object({
  phraseText: z.string().min(1).max(500).optional(),
  phraseType: PhraseTypeSchema.optional(),
  matchType: MatchTypeSchema.optional(),
  semanticThreshold: z.number().min(0).max(1).optional(),
  weight: z.number().int().min(SCRIPT_WEIGHT_MIN).max(SCRIPT_WEIGHT_MAX).optional(),
});

export const CreateStepRequestSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(1000).optional(),
  isRequired: z.boolean().default(true),
  orderEnforced: z.boolean().default(false),
  weight: z
    .number()
    .int()
    .min(SCRIPT_WEIGHT_MIN)
    .max(SCRIPT_WEIGHT_MAX)
    .default(SCRIPT_WEIGHT_NORMAL),
  phrases: z.array(CreatePhraseRequestSchema).optional(),
});

export const UpdateStepRequestSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  description: z.string().max(1000).optional(),
  isRequired: z.boolean().optional(),
  orderEnforced: z.boolean().optional(),
  weight: z.number().int().min(SCRIPT_WEIGHT_MIN).max(SCRIPT_WEIGHT_MAX).optional(),
});

export const CreateScriptRequestSchema = z.object({
  name: z.string().min(1).max(255),
  description: z.string().max(1000).optional(),
  category: ScriptCategorySchema,
  steps: z.array(CreateStepRequestSchema).optional(),
});

export const UpdateScriptRequestSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  description: z.string().max(1000).optional(),
  category: ScriptCategorySchema.optional(),
  isActive: z.boolean().optional(),
});

export const ReorderStepsRequestSchema = z.object({
  stepIds: z.array(z.string().uuid()).min(1),
});

export const AssignScriptRequestSchema = z.object({
  scriptId: z.string().uuid(),
  isDefault: z.boolean().default(false),
});

// ============================================================================
// Filter Schemas
// ============================================================================

export const ScriptFilterSchema = z.object({
  category: ScriptCategorySchema.optional(),
  isActive: z.boolean().optional(),
  search: z.string().max(255).optional(),
});

export const ScriptAdherenceFilterSchema = z.object({
  scriptId: z.string().uuid().optional(),
  adherenceScoreMin: z.number().int().min(0).max(100).optional(),
  adherenceScoreMax: z.number().int().min(0).max(100).optional(),
  dateFrom: z.string().datetime().optional(),
  dateTo: z.string().datetime().optional(),
});

// ============================================================================
// Type Exports (with Inferred suffix to avoid conflicts with types/)
// ============================================================================

// Note: Main types are exported from types/script-adherence.ts
// These inferred types are for schema validation results
export type ScriptCategoryInferred = z.infer<typeof ScriptCategorySchema>;
export type PhraseTypeInferred = z.infer<typeof PhraseTypeSchema>;
export type MatchTypeInferred = z.infer<typeof MatchTypeSchema>;
export type SpeakerInferred = z.infer<typeof SpeakerSchema>;
